// composables/useDocketLogic.js

export function useDocketLogic() {
  
  // --- HELPER: Add Duration (Supports Days 'D', Weeks 'W', Months 'M') ---
  function addDuration(date, durationStr) {
    if (!date) return null;
    const result = new Date(date);
    
    if (!durationStr || durationStr === 'Custom') return result;

    const match = durationStr.match(/^(\d+(\.\d+)?)([DWM])$/);
    if (!match) return result;

    const value = parseFloat(match[1]);
    const unit = match[3];

    if (unit === 'D') {
      result.setDate(result.getDate() + value);
    } else if (unit === 'W') {
      result.setDate(result.getDate() + (value * 7));
    } else if (unit === 'M') {
      const wholeMonths = Math.floor(value);
      const partialMonth = value - wholeMonths;
      result.setMonth(result.getMonth() + wholeMonths);
      if (partialMonth > 0) {
        result.setDate(result.getDate() + Math.round(partialMonth * 30));
      }
    }
    return result;
  }

  // --- HELPER: Is this an "Action!" extension? ---
  function isActionExtension(ext) {
    return typeof ext === 'string' && ext.startsWith('Action!');
  }

  // --- MAIN LOGIC: Calculate Final Due Date ---
  function getCalculatedDate(item) {
    let calculatedDate;

    // Helper to safely convert Firestore Timestamps or JS Dates
    const toDate = (val) => {
      if (!val) return null;
      if (val.seconds !== undefined) return new Date(val.seconds * 1000);
      return new Date(val);
    };

    const startDate = toDate(item.startDate) || toDate(item.timestamp) || new Date();

    // Establish Baseline
    if (item.baseDuration === 'Custom' && item.customDate) {
      calculatedDate = toDate(item.customDate) || startDate;
    } else {
      calculatedDate = addDuration(startDate, item.baseDuration);
    }

    // Apply Extensions (Runs for ALL types, including Custom)
    if (item.extensions && Array.isArray(item.extensions)) {
      item.extensions.forEach(ext => {
        if (!isActionExtension(ext)) {
          calculatedDate = addDuration(calculatedDate, ext);
        }
      });
    }

    return calculatedDate;
  }

  // --- NEW HELPER: Sanitize for Firestore (Fixes Problem B) ---
  // This centralizes the removal of "Dirty Data" before saving
  function sanitizeDocketItem(item) {
    const {
      calculatedDateObj,
      dueDate,
      activity,
      pendingActivity,
      state,
      agencyInfo,
      isActionItem,
      silent,
      hotStatus,
      ...cleanData
    } = item;

    // Remove any keys that are undefined to prevent Firestore crashes
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === undefined) {
        delete cleanData[key];
      }
    });

    return cleanData;
  }

  // --- HELPER: Date Color ---
  function getDateColorClass(dateObj) {
    if (!dateObj) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateObj);
    target.setHours(0, 0, 0, 0);

    if (target < today) return 'text-red font-weight-bold';
    return 'text-green-darken-2 font-weight-bold';
  }

  // --- Status Helpers ---
  function getHotIcon(status) {
    if (status === 4) return 'mdi-close-circle';
    if (status === 3) return 'mdi-snowflake';
    return 'mdi-fire';
  }

  function getHotColor(status) {
    if (status === 2) return 'red-accent-4';
    if (status === 1) return 'orange';
    if (status === 3) return 'blue';
    if (status === 4) return 'red';
    return 'grey-lighten-1';
  }

  function getHotTooltip(status) {
      if (status === 2) return 'Hot Agency';
      if (status === 1) return 'Good Agency';
      if (status === 3) return 'Cool — Prefer Not to Use';
      if (status === 4) return 'Avoid';
      return 'Standard Agency';
  }

  function getNextHotStatus(current) {
    return (current + 1) % 5;
  }

  return {
    addDuration,
    isActionExtension,
    getCalculatedDate,
    sanitizeDocketItem, // Exporting the new cleanup tool
    getDateColorClass,
    getHotIcon,
    getHotColor,
    getHotTooltip,
    getNextHotStatus
  };
}